/** Reassemble Shopify-hosted archive parts and verify the original file hash. */
export async function buildSupportDownload(record, fetchFile = fetch, cryptoApi = crypto) {
  if (!record || !Array.isArray(record.parts) || record.parts.length < 1 || record.parts.length > 4 ||
      !Number.isSafeInteger(record.size) || record.size <= 0 || !/^[a-f0-9]{64}$/.test(record.sha256) ||
      typeof record.filename !== 'string' || /[\\/]/.test(record.filename) || !record.filename.endsWith('.zip')) {
    throw new Error('Invalid download record');
  }
  const buffers = await Promise.all(record.parts.map(async (part) => {
    const url = new URL(part);
    if (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com') throw new Error('Invalid download host');
    const response = await fetchFile(url.href);
    if (!response.ok) throw new Error('Download request failed');
    return new Uint8Array(await response.arrayBuffer());
  }));
  if (buffers.reduce((total, part) => total + part.length, 0) !== record.size) throw new Error('Incomplete download');
  const combined = new Uint8Array(record.size);
  let offset = 0;
  for (const part of buffers) { combined.set(part, offset); offset += part.length; }
  const hash = await cryptoApi.subtle.digest('SHA-256', combined);
  const hex = Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, '0')).join('');
  if (hex !== record.sha256) throw new Error('Download checksum mismatch');
  return new Blob([combined], { type: 'application/zip' });
}

async function initializeSupportDownload(root) {
  const status = root.querySelector('[data-download-status]');
  const link = root.querySelector('[data-download-link]');
  const retry = root.querySelector('[data-download-retry]');
  retry.addEventListener('click', () => window.location.reload());
  try {
    const response = await fetch(root.dataset.beamDownloadIndex);
    if (!response.ok) throw new Error('Download index unavailable');
    const index = await response.json();
    const id = new URLSearchParams(window.location.search).get('file');
    const record = Object.hasOwn(index, id) ? index[id] : null;
    if (!record) {
      status.textContent = 'Select a download from product support to continue.';
      return;
    }
    status.textContent = `Preparing ${record.filename}…`;
    const blob = await buildSupportDownload(record);
    const blobUrl = URL.createObjectURL(blob);
    link.href = blobUrl;
    link.download = record.filename;
    link.textContent = `Download ${record.filename}`;
    link.hidden = false;
    status.textContent = 'Your download is ready. If it does not start automatically, select Download.';
    link.click();
    window.addEventListener('pagehide', () => URL.revokeObjectURL(blobUrl), { once: true });
  } catch {
    status.textContent = 'We could not prepare your download. Please try again.';
    retry.hidden = false;
  }
}

if (typeof document !== 'undefined') {
  for (const root of document.querySelectorAll('[data-beam-download-index]')) initializeSupportDownload(root);
}
